import { ChevronDown, GitCommitHorizontal } from "lucide-react"
import { type ReactNode, useId, useState } from "react"
import { useT } from "@/lib/i18n"
import { cn } from "@/lib/utils"

export type CellKind = "source" | "transform" | "match" | "review" | "chart" | "promote"

const KIND_LABEL = {
  source: "wb.kind.source",
  transform: "wb.kind.transform",
  match: "wb.kind.match",
  review: "wb.kind.review",
  chart: "wb.kind.chart",
  promote: "wb.kind.promote",
} as const

const KIND_TONE: Record<CellKind, string> = {
  source: "text-[var(--lineage)]",
  transform: "text-foreground",
  match: "text-foreground",
  review: "text-warning",
  chart: "text-success",
  promote: "text-[var(--lineage)]",
}

/** One column's lineage: where its values come from, and what a value means. */
export interface ColumnLineage {
  readonly column: string
  readonly from: ReactNode
  readonly means: string
}

/**
 * A notebook cell: numbered, typed, titled, with a one-line result summary in its header, its
 * output below, and its lineage one click away. Cells read top to bottom; each uses the ones above.
 */
export function Cell({
  index,
  kind,
  title,
  summary,
  chain,
  lineage,
  actions,
  children,
  id,
}: {
  index: number
  kind: CellKind
  title: string
  summary?: ReactNode
  /** Upstream steps, oldest first (source system → mapping → type → this cell). */
  chain: readonly string[]
  lineage: readonly ColumnLineage[]
  actions?: ReactNode
  children: ReactNode
  id?: string
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const panel = useId()
  return (
    <section id={id} aria-labelledby={`${panel}-title`} className="group/cell relative grid grid-cols-[2.5rem_minmax(0,1fr)] gap-x-3 scroll-mt-20">
      <div className="flex flex-col items-center pt-1">
        <span className="grid size-7 place-items-center rounded-full border border-border bg-background text-xs font-medium tabular-nums">{index}</span>
        <span className="mt-1 w-px flex-1 bg-border group-last/cell:hidden" aria-hidden />
      </div>
      <div className="min-w-0 space-y-3 pb-10">
        <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className={cn("text-2xs font-semibold tracking-wide uppercase", KIND_TONE[kind])}>{t(KIND_LABEL[kind])}</span>
          <h2 id={`${panel}-title`} className="text-[15px] font-semibold tracking-tight">
            {title}
          </h2>
          {summary ? <span className="text-xs text-muted-foreground">{summary}</span> : null}
          <span className="ms-auto flex items-center gap-1.5">
            {actions}
            <button
              type="button"
              aria-expanded={open}
              aria-controls={`${panel}-lineage`}
              onClick={() => setOpen((o) => !o)}
              className={cn(
                "inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/30",
                open ? "border-[var(--lineage)]/50 bg-[var(--lineage)]/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground",
              )}
            >
              <GitCommitHorizontal className="size-3.5 text-[var(--lineage)]" aria-hidden />
              {t("wb.lineage.toggle")}
            </button>
            <button
              type="button"
              aria-expanded={!collapsed}
              aria-label={collapsed ? t("wb.cell.expand") : t("wb.cell.collapse")}
              onClick={() => setCollapsed((c) => !c)}
              className="grid size-7 place-items-center rounded-full text-muted-foreground outline-none hover:bg-muted/60 hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/30"
            >
              <ChevronDown className={cn("size-4 transition-transform", collapsed && "-rotate-90 rtl:rotate-90")} aria-hidden />
            </button>
          </span>
        </header>
        {open ? <LineagePanel id={`${panel}-lineage`} chain={chain} lineage={lineage} /> : null}
        {collapsed ? null : children}
      </div>
    </section>
  )
}

/** The cell's provenance: a straight chain from the source system to this cell, then column by column. */
function LineagePanel({ id, chain, lineage }: { id: string; chain: readonly string[]; lineage: readonly ColumnLineage[] }) {
  const t = useT()
  return (
    <div id={id} className="space-y-3 rounded-xl border border-dashed border-[var(--lineage)]/50 bg-[var(--lineage)]/[0.04] p-3.5">
      <ol aria-label={t("wb.lineage.chain")} className="flex flex-wrap items-center gap-1.5 text-xs">
        {chain.map((step, i) => (
          <li key={`${i}-${step}`} className="flex items-center gap-1.5">
            {i > 0 ? <span className="text-[var(--lineage)]" aria-hidden>→</span> : null}
            <span dir="auto" className={cn("rounded-md border px-2 py-0.5", i === chain.length - 1 ? "border-[var(--lineage)]/60 font-medium" : "border-border bg-background font-mono text-2xs")}>
              {step}
            </span>
          </li>
        ))}
      </ol>
      <table className="w-full text-start text-xs">
        <thead>
          <tr className="text-muted-foreground">
            <th className="py-1 pe-3 text-start font-medium">{t("wb.lineage.column")}</th>
            <th className="py-1 pe-3 text-start font-medium">{t("wb.lineage.from")}</th>
            <th className="py-1 text-start font-medium">{t("wb.lineage.means")}</th>
          </tr>
        </thead>
        <tbody>
          {lineage.map((l) => (
            <tr key={l.column} className="border-t border-border/60 align-top">
              <td className="py-1.5 pe-3 font-medium whitespace-nowrap">{l.column}</td>
              <td dir="ltr" className="py-1.5 pe-3 font-mono text-2xs text-muted-foreground">
                {l.from}
              </td>
              <td className="py-1.5 text-muted-foreground">{l.means}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
